import { jwtDecode } from 'jwt-decode';
import { AUTH_STORAGE_KEYS, clearSessionStorageKeys } from '../constants';
import { clearAuthStorage, getCookie } from './cookies';

interface JwtPayload {
  exp: number;
  user_id?: string;
  iat?: number;
  mega?: boolean;
  actor_user_id?: string;
}

/**
 * Token Provider - Centralized auth token management
 *
 * Provides functions for token retrieval, validation, and lifecycle management.
 * These functions are injected into api-client via dependency injection.
 */

/**
 * Retrieves the auth token using cookies first, localStorage as fallback
 * Enables cross-port authentication during development
 * @returns Auth token or null if not found
 */
export function getAuthToken(): string | null {
  // Try to get token from cookies first (for cross-port authentication)
  const cookieToken = getCookie(AUTH_STORAGE_KEYS.TOKEN);
  if (cookieToken) {
    return cookieToken;
  }

  // Fallback to localStorage for existing sessions
  const localStorageToken = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
  return localStorageToken;
}

/**
 * Validates a JWT token's structure and expiration
 * @param token - JWT token to validate
 * @returns Object with isValid flag and optional error message
 */
export function validateToken(token: string): { isValid: boolean; error?: string } {
  try {
    // Validate token structure (JWT format: header.payload.signature)
    const parts = token.split('.');
    if (parts.length !== 3) {
      return { isValid: false, error: 'Invalid token format' };
    }

    // Decode and validate token expiration
    const decoded: JwtPayload = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);

    if (decoded.exp && decoded.exp < currentTime) {
      return { isValid: false, error: 'Token expired' };
    }

    return { isValid: true };
  } catch (_error) {
    return { isValid: false, error: 'Token validation failed' };
  }
}

/**
 * Retrieves and validates the auth token
 * Returns null if token is invalid or expired
 * Automatically clears invalid tokens from storage
 * @returns Valid token or null
 */
export function getValidatedAuthToken(): string | null {
  const shouldLog =
    typeof import.meta !== 'undefined' &&
    typeof import.meta.env !== 'undefined' &&
    !!import.meta.env.VITE_DEBUG_TOKEN_PROVIDER;

  const log = (...args: any[]) => {
    if (shouldLog) {
      // eslint-disable-next-line no-console
      console.warn(...args);
    }
  };

  const token = getAuthToken();
  log('[TokenProvider] Retrieved token:', token ? `${token.substring(0, 20)}...` : 'null');
  log('[TokenProvider] Cookie token:', getCookie(AUTH_STORAGE_KEYS.TOKEN) ? 'exists' : 'none');
  log(
    '[TokenProvider] LocalStorage token:',
    localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN) ? 'exists' : 'none'
  );

  if (!token) {
    log('[TokenProvider] No token found in storage');
    return null;
  }

  const validation = validateToken(token);

  if (!validation.isValid) {
    log('[TokenProvider] Token validation failed:', validation.error);
    localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN);
    return null;
  }

  return token;
}

/**
 * Handles token expiration
 * Clears all auth storage and redirects to session expired page
 */
export function handleTokenExpired(): void {
  const shouldLog =
    typeof import.meta !== 'undefined' &&
    typeof import.meta.env !== 'undefined' &&
    !!import.meta.env.VITE_DEBUG_TOKEN_PROVIDER;
  if (shouldLog) {
    // eslint-disable-next-line no-console
    console.warn('[TokenProvider] Token expired, clearing session');
  }
  clearSessionStorageKeys();
  window.location.href = '/session-expired';
}

/**
 * Handles unauthorized responses (401)
 * Clears all auth storage and redirects to login page
 */
export async function handleUnauthorized(): Promise<void> {
  const shouldLog =
    typeof import.meta !== 'undefined' &&
    typeof import.meta.env !== 'undefined' &&
    !!import.meta.env.VITE_DEBUG_TOKEN_PROVIDER;
  if (shouldLog) {
    // eslint-disable-next-line no-console
    console.warn('[TokenProvider] Unauthorized, clearing session and redirecting to login');
  }

  // Clear all auth storage (both localStorage and cookies)
  clearSessionStorageKeys();
  await clearAuthStorage(AUTH_STORAGE_KEYS);

  // Navigate to login page
  // In development (different ports), use full URL
  // In production (monolithic build), use relative path
  const shellUrl =
    typeof import.meta !== 'undefined' &&
    typeof import.meta.env !== 'undefined' &&
    import.meta.env.VITE_SHELL_URL
      ? import.meta.env.VITE_SHELL_URL
      : '';

  // Determine if we need a full URL or relative path
  const loginUrl = shellUrl ? `${shellUrl}/login` : '/login';

  window.location.href = loginUrl;
}

/**
 * Clears the auth token from storage
 * Useful for logout or manual token cleanup
 */
export function clearAuthToken(): void {
  localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN);
}

/**
 * Returns megamode details if the active session is an megamode
 * token (carries the `mega` claim), otherwise null.
 */
export function getMegaModeInfo(): { actorUserId?: string } | null {
  const token = getAuthToken();
  if (!token) {
    return null;
  }
  try {
    const decoded: JwtPayload = jwtDecode(token);
    return decoded.mega ? { actorUserId: decoded.actor_user_id } : null;
  } catch (_error) {
    return null;
  }
}
